import { api } from '@core/api/client'
import {
  lerAviso,
  lerItem,
  type AlteracaoParaEnvio,
  type AvisoDoServidor,
  type ItemPartilhado,
} from './clashPartilha'

// ---------------------------------------------------------------------------
// O cano da triagem partilhada: os pedidos a API. O que decide a ordem
// das coisas esta em clashPartilhaMotor.ts, testavel sem rede.
// ---------------------------------------------------------------------------

export const BASE_DA_PARTILHA = '/api/modules/clash/partilha'

export interface ModeloDaTriagem {
  ficheiro: string
  sha256: string
}

/** Uma triagem partilhada do workspace (como a lista a mostra). */
export interface ProjetoDeConflitos {
  id: string
  codigo: string
  nome: string | null
  modelos: ModeloDaTriagem[]
  versao: number
  conflitos: number
  criado_por: string | null
  criado_em: string
  actualizado_em: string
  /** A partilha (lista de acessos) e a casa. Ver docs/PARTILHA.md. */
  partilha_id?: string | null
  workspace_nome?: string | null
  pode_gerir?: boolean
  visivel_para?: string | null
}

export interface LeituraDaPartilha {
  versao: number
  itens: ItemPartilhado[]
}

export interface RespostaDoEnvio {
  versao: number
  aplicadas: number
  avisos: AvisoDoServidor[]
  actuais: ItemPartilhado[]
  invalidas: { tipo: string; chave: string; motivo: string }[]
}

/** O que o motor precisa da API (trocavel nos testes). */
export interface PortaDaPartilha {
  ler: (projetoId: string, desde: number) => Promise<LeituraDaPartilha>
  enviar: (projetoId: string, lido: number, alteracoes: AlteracaoParaEnvio[]) => Promise<RespostaDoEnvio>
}

function lista<T>(v: unknown, ler: (x: unknown) => T | null): T[] {
  return Array.isArray(v) ? v.map(ler).filter((x): x is T => x !== null) : []
}

export const portaHttp: PortaDaPartilha = {
  ler: async (projetoId, desde) => {
    const r = await api.get<{ versao: number; itens: unknown[] }>(
      `${BASE_DA_PARTILHA}/projetos/${encodeURIComponent(projetoId)}/alteracoes?desde=${desde}`,
    )
    return { versao: Number(r.versao) || 0, itens: lista(r.itens, lerItem) }
  },
  enviar: async (projetoId, lido, alteracoes) => {
    const r = await api.post<Record<string, unknown>>(
      `${BASE_DA_PARTILHA}/projetos/${encodeURIComponent(projetoId)}/alteracoes`,
      { lido, alteracoes },
    )
    return {
      versao: Number(r.versao) || 0,
      aplicadas: Number(r.aplicadas) || 0,
      avisos: lista(r.avisos, lerAviso),
      actuais: lista(r.actuais, lerItem),
      invalidas: Array.isArray(r.invalidas)
        ? (r.invalidas as { tipo: string; chave: string; motivo: string }[])
        : [],
    }
  },
}

export function listarTriagens(): Promise<ProjetoDeConflitos[]> {
  return api.get<ProjetoDeConflitos[]>(`${BASE_DA_PARTILHA}/projetos`)
}

export function criarTriagem(o: {
  codigo: string
  nome?: string | null
  modelos: ModeloDaTriagem[]
  /** Falso: a triagem fica só para quem a partilha, que escolhe depois
   *  as pessoas em "Partilhar com...". */
  workspace_inteiro?: boolean
}): Promise<ProjetoDeConflitos> {
  return api.post<ProjetoDeConflitos>(`${BASE_DA_PARTILHA}/projetos`, o)
}

/** Uma triagem, para quem lhe tem acesso (de qualquer workspace). */
export function verTriagem(projetoId: string): Promise<ProjetoDeConflitos> {
  return api.get<ProjetoDeConflitos>(`${BASE_DA_PARTILHA}/projetos/${encodeURIComponent(projetoId)}`)
}

export function actualizarModelos(projetoId: string, modelos: ModeloDaTriagem[]): Promise<ProjetoDeConflitos> {
  return api.patch<ProjetoDeConflitos>(`${BASE_DA_PARTILHA}/projetos/${encodeURIComponent(projetoId)}`, {
    modelos,
  })
}

/** O endereco do canal ao vivo: o da API com o esquema trocado. */
export function urlDoCanal(
  projetoId: string,
  base: string = import.meta.env.VITE_API_BASE_URL ?? '',
): string {
  const raiz = base || window.location.origin
  const ws = raiz.replace(/^http:/, 'ws:').replace(/^https:/, 'wss:')
  return `${ws.replace(/\/$/, '')}${BASE_DA_PARTILHA}/projetos/${encodeURIComponent(projetoId)}/canal`
}

/** Quantos modelos da triagem estao abertos (pelo sha256, ou pelo nome
 *  quando o sha256 nao se conhece). */
export function modelosEmComum(
  triagem: readonly ModeloDaTriagem[],
  abertos: readonly { fileName: string; sha256: string }[],
): number {
  let n = 0
  for (const m of triagem) {
    const igual = abertos.some((a) =>
      m.sha256 && a.sha256 ? m.sha256 === a.sha256 : m.ficheiro === a.fileName,
    )
    if (igual) n += 1
  }
  return n
}
